import { forwardRef } from 'react';
import { TextInput, type TextInputProps } from './Field';
import { cn } from './cn';

/** Authenticator apps (RFC 6238 as be-tepco verifies it) issue six digits. */
export const ONE_TIME_CODE_LENGTH = 6;

/**
 * There is deliberately no `maxLength` on the field: the browser would cut a pasted
 * "Code: 123 456" before this runs. Length is enforced here instead.
 *
 * Keeps the digits of whatever was typed or pasted — "123 456", "123-456" and a code copied
 * with a trailing newline all become "123456" — and drops anything past the code's length.
 */
export function normalizeOneTimeCode(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, ONE_TIME_CODE_LENGTH);
}

export interface OneTimeCodeInputProps
  extends Omit<TextInputProps, 'value' | 'onChange' | 'type' | 'inputMode' | 'maxLength'> {
  value: string;
  onChange: (code: string) => void;
  /**
   * Called once the sixth digit lands from typing, pasting or the OS one-time-code autofill,
   * so the caller can submit without a second gesture. Not called on mount with a full value.
   */
  onComplete?: (code: string) => void;
}

/**
 * The 6-digit authenticator code field: numeric keypad on touch devices, OS/password-manager
 * one-time-code autofill, digits-only, tabular mono numerals (DESIGN Tabular Numerals rule)
 * spaced so a code can be checked against the app at a glance. Digits always run
 * left-to-right, in the Arabic UI too, matching how authenticator apps display them.
 */
export const OneTimeCodeInput = forwardRef<HTMLInputElement, OneTimeCodeInputProps>(
  function OneTimeCodeInput({ value, onChange, onComplete, className, ...rest }, ref) {
    return (
      <TextInput
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        spellCheck={false}
        dir="ltr"
        className={cn('tnum font-mono text-[1.375rem] leading-[1.25] tracking-[0.42em] text-ink-deep', className)}
        value={value}
        onChange={(e) => {
          const next = normalizeOneTimeCode(e.target.value);
          onChange(next);
          if (next.length === ONE_TIME_CODE_LENGTH && value.length !== ONE_TIME_CODE_LENGTH) {
            onComplete?.(next);
          }
        }}
        {...rest}
      />
    );
  },
);
